import { createContext, useCallback, useEffect, useState } from 'react';
import { loadStoredToken, clearStoredToken } from '../utils/tokenStore';
import { isLocalLogout, login, logout } from '../services/auth';
import { trackEvent, setAuthenticatedUserContext } from '../services/telemetry';

export const AuthContext = createContext(null);

/**
 * Mock profile used when running the admin SPA locally without SWA auth.
 * Set `VITE_DEV_BYPASS_AUTH=true` in .env.development to enable.
 */
const DEV_PROFILE = {
  name: 'Dev User',
  email: 'dev@journiy.local',
  userId: 'dev-user',
  token: 'dev-token',
  identity_provider: 'local',
  role: 'admin',
  adminLevel: 2,
  dataManagement: true,
};

/**
 * Loads the SWA auth profile (/.auth/me) once on mount and exposes the
 * authenticated user, their id_token, and login/logout actions.
 *
 * When `VITE_DEV_BYPASS_AUTH` is true, skips the auth check and
 * returns a mock profile so ProtectedRoute doesn't redirect.
 */
export function AuthProvider({ children }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    // Local dev: skip SWA auth entirely so ProtectedRoute passes through.
    if (import.meta.env.VITE_DEV_BYPASS_AUTH === 'true' && !isLocalLogout()) {
      setProfile(DEV_PROFILE);
      setError(null);
      setLoading(false);
      return DEV_PROFILE;
    }

    try {
      const p = loadStoredToken();
      if (p && p.role !== 'admin') {
        clearStoredToken();
        throw new Error('Administrator access is required.');
      }
      setProfile(p);
      setError(null);
      if (p) {
        trackEvent('admin_auth_ok', { email: p.email || undefined });
        setAuthenticatedUserContext(p.email || p.name || p.id || 'unknown');
      }
      return p;
    } catch (err) {
      setError(err?.message || String(err));
      setProfile(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // A 401/403 response means the stored token is dead. Drop the
  // in-memory profile so ProtectedRoute shows the sign-in screen
  // (with message + "Sign in again") instead of an empty layout.
  useEffect(() => {
    const onTokenCleared = () => {
      if (import.meta.env.VITE_DEV_BYPASS_AUTH === 'true') return;
      setProfile(null);
      setError('Your sign-in session expired. Please sign in again.');
      setLoading(false);
    };
    window.addEventListener('journiy:admin-token-cleared', onTokenCleared);
    return () => window.removeEventListener('journiy:admin-token-cleared', onTokenCleared);
  }, []);

  const value = {
    profile,
    token: profile?.token || null,
    isAuthenticated: Boolean(profile),
    loading,
    error,
    refresh,
    login,
    logout: () => {
      clearStoredToken();
      setProfile(null);
      logout();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
